import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, FormControl, InputLabel, Select } from '@mui/material';
import { MenuItem } from '@mui/material';
import { getIntegrationAppLabel } from 'helpers';
import {
  EstateBalanceSheetAsset_GetEstateBalanceSheetAssetsByEstateBalanceSheetIdDocument,
  EstateBalanceSheetLiability_GetEstateBalanceSheetLiabilitiesByEstateBalanceSheetIdDocument,
  IntegrationApp,
  useDecisionVault_SyncMatterAssetsMutation,
  useEstateBalanceSheet_GetEstateBalanceSheetByEstateIdQuery,
  useIntegration_GetIntegrationsByPortalIdQuery,
} from 'queries';

import Modal from 'common/modal';
import Spinner from 'common/spinner';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';

import { useStyles } from './estate-balance-sheet-integrations-modal.styles';

type Props = {
  showModal: boolean;
  setShowModal: (value: boolean) => void;
};

export const EstateBalanceSheetIntegrationsModal = ({ showModal, setShowModal }: Props) => {
  const classes = useStyles();
  const { estateId, portalId } = useParams<Record<string, string>>();

  const [selectedApp, setSelectedApp] = useState<IntegrationApp | null>();
  const [syncInProgress, setSyncInProgress] = useState(false);

  const { data: estateBalanceSheetData, loading: estateBalanceSheetLoading } =
    useEstateBalanceSheet_GetEstateBalanceSheetByEstateIdQuery({
      variables: { input: { estateId, portalId } },
    });

  const [syncDecisionVaultMatterAssets] = useDecisionVault_SyncMatterAssetsMutation({
    refetchQueries: [
      {
        query: EstateBalanceSheetAsset_GetEstateBalanceSheetAssetsByEstateBalanceSheetIdDocument,
        variables: { input: { estateBalanceSheetId: estateBalanceSheetData?.data.id, portalId } },
      },
      {
        query:
          EstateBalanceSheetLiability_GetEstateBalanceSheetLiabilitiesByEstateBalanceSheetIdDocument,
        variables: { input: { estateBalanceSheetId: estateBalanceSheetData?.data.id, portalId } },
      },
    ],
    onCompleted: () => {
      setSyncInProgress(false);
      showAlert({
        message: `Matter assets have been synced successfully.`,
        severity: NotificationSeverity.SUCCESS,
      });
      setShowModal(false);
    },
    onError: (error: Error) => {
      showAlert({
        message: `There was an issue syncing your matter's assets: ${error.message}`,
        severity: NotificationSeverity.ERROR,
      });
      setSyncInProgress(false);
    },
  });

  const { data: integrationsData, loading: integrationsDataLoading } =
    useIntegration_GetIntegrationsByPortalIdQuery({
      variables: {
        input: {
          portalId,
        },
      },
    });

  const onExitClick = () => {
    setShowModal(false);
  };

  const handleOnSyncButtonClick = () => {
    if (!selectedApp) {
      return;
    }
    setSyncInProgress(true);
    switch (selectedApp) {
      case IntegrationApp.DecisionVault:
        syncDecisionVaultMatterAssets({
          variables: {
            input: {
              estateId,
              portalId,
            },
          },
        });
        break;
      default:
        console.error('Unsupported integration app:', selectedApp);
        setSyncInProgress(false);
        break;
    }
  };

  // integrations are currently locked to the integration used to import the matter
  // in the future we may have integrations unrelated to the actual import and will want to remove
  // this restriction
  const selectOptions = () => {
    if (integrationsDataLoading || !integrationsData?.data) {
      return [];
    }

    return integrationsData.data.map((integration) => (
      <MenuItem key={integration.app} value={integration.app}>
        {getIntegrationAppLabel(integration.app)}
      </MenuItem>
    ));
  };

  if (estateBalanceSheetLoading) {
    return <Spinner />;
  }

  const getEstateBalanceSheetIntegrationsModalContent = () => {
    return (
      <>
        {syncInProgress ? (
          <Spinner />
        ) : (
          <Box className={classes.container}>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="integration-select-label">Select an integration</InputLabel>
              <Select
                className={classes.select}
                labelId="integration-select-label"
                label="Select an integration"
                value={selectedApp || ''}
                onChange={(evt) => {
                  setSelectedApp(evt.target.value as IntegrationApp);
                }}
              >
                {selectOptions()}
              </Select>
            </FormControl>
          </Box>
        )}
      </>
    );
  };

  return (
    <Modal
      showModal={showModal}
      setShowModal={setShowModal}
      actionVerb="Sync"
      onExitClick={onExitClick}
      onActionClick={handleOnSyncButtonClick}
      content={getEstateBalanceSheetIntegrationsModalContent()}
    />
  );
};
